import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  dismissQaScrubberItemVisitsVisitIdQaScrubberRuleIdDismissPost,
  getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet,
  listEnabledSectionsBespokeTemplatesEnabledGet,
  restoreQaScrubberItemVisitsVisitIdQaScrubberRuleIdDismissDelete,
  validateOasisVisitsVisitIdValidateOasisPost,
} from '../../services/api/sdk.gen';
import { VisitTypeId } from '../../services/api/models/VisitTypeId';
import {
  OASISSchema,
  OASISFormData,
  Question,
  FormSchemaVersion,
} from './types';
import type {
  ContradictionFieldRef,
  ContradictionFinding,
  EnabledBespokeEntry,
  OasisValidationError,
  RogerDocFormType,
} from '../../services/api/types.gen';
import { OASISQuestionRenderer } from './OASISQuestionRenderer';
import { ClinicProvider } from './ClinicContext';
import { FormTemplatesProvider } from './FormTemplatesContext';
import { useAgencyFormTemplates } from './useAgencyFormTemplates';
import { OASISNavigation } from './OASISNavigation';
import { VisitActiveDiagnosis } from './VisitActiveDiagnosis';
import { DiagnosisReview } from './DiagnosisReview';
import { CmsIssueCard } from './CmsIssueCard';
import { ContradictionFindingCard } from './contradictions/ContradictionFindingCard';
import {
  findContradictionSourceTargets,
  sectionIdForContradictionSource,
} from './contradictions/contradictionNavigation';
import {
  buildAdvisoryScrubberItems,
  buildFatalScrubberErrors,
  cmsErrorIsDiagnosisSeverity,
  countActiveScrubberItems,
  countCmsWarningsByCategory,
  countFatalsByCategory,
  countQaFindingsByCategory,
  SCRUBBER_TAB_DEFINITIONS,
  SCRUBBER_TABS,
} from './contradictions/scrubberAdvisory';
import type {
  ScrubberListItem,
  ScrubberTab,
} from './contradictions/scrubberAdvisory';
import {
  escapeCssAttributeValue,
  isVisibleOasisNavigationTarget,
} from './oasisNavigationVisibility';
import { centerInScrollPort } from '../../utils/scrollPort';
import {
  resolveBespokeSection,
  type SchemaQuestionOption,
  type BespokeDbConfig,
} from './bespoke/bespokeRegistry';
import { useVisitExternalAnswers } from './bespoke/layout/useVisitExternalAnswers';
import { pinFromFormSchemaVersion } from '../../pages/formSchema/formSchemaVersionPin';
import { useOASISFormStore } from '../../store/slices/oasisFormSlice';
import { useDebounce } from '../../hooks/useDebounce';
import { useToast } from '../../hooks/useToastContext';
import { useScrollportHeight } from '../../hooks/useScrollportHeight';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { getOASISFormData, getSubitemClearUpdates } from './utils';
import {
  resolveQuestionSpan,
  getOasisAutosaveDebounceMs,
  OASIS_INLINE_VALIDATION_DEBOUNCE_MS,
  GridSpan,
  SPAN_CLASSES,
} from './OASISFormUtils';
import { Loader2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { CarryoverSourceMenu } from '../visit/CarryoverSourceMenu';
import { CarryoverStagedBanner } from '../visit/CarryoverStagedBanner';
import { SaveDeclinedError, useSaveStatus } from '../../hooks/useAutoSave';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../../constants/featureFlags';
import { NON_OASIS_WHOLE_VISIT_SECTION_NAME } from '../../constants/nonOasisFormSchema';
import {
  showsEventsSection,
  showsDiagnosisReview,
  showsGoalsSection,
  isSNDiscipline,
  supportsActiveDiagnosisPage,
  supportsWounds,
  getDisciplineForVisitType,
} from '../../constants/visitTypes';
import { visitGoalCapabilities } from '../../utils/goalTaskCapabilities';
import {
  ACTIVE_DIAGNOSIS_CATEGORY,
  MEDICATIONS_SECTION_ID,
  SKIN_SECTION_ID,
  EVENTS_CATEGORY,
  GOALS_CATEGORY,
  buildLabelByCategory,
  resolveCategoryLabel,
} from './OASISFormLabels';
import { PatientWounds } from '../wounds';
import { PatientMedications } from '../medications';
import PatientAllergies from '../PatientAllergies';
import { PatientEvents } from '../patient/PatientEvents';
import GoalsTab, { type GoalsTabVisit } from '../goals/GoalsTab';
import type {
  OrderResponse,
  PatientResponse,
  TaskStatus,
} from '../../services/api/types.gen';

/**
 * Height bounds for the expanded Live Scrubber issue list, in pixels so they
 * stay in the same units as the measured scrollport height. A rem-based cap
 * would disagree with the measured one for anyone who has raised their browser
 * font size, leaving two competing ceilings.
 */
const SCRUBBER_BODY_MAX_HEIGHT_PX = 224;
const SCRUBBER_BODY_MIN_HEIGHT_PX = 96;
/** Share of the visible form area the expanded list may take at most. */
const SCRUBBER_BODY_SCROLLPORT_SHARE = 0.35;

/**
 * Height the pinned section list and Live Scrubber hold back from the
 * scrollport. It has to be at least the bottom padding of the scrolled content
 * that hosts this form, which is `p-5` (20px) on the body of
 * `VisitDetailPanel`; see `useScrollportHeight` for why a smaller value clips
 * the pinned panel's own header at the end of a long form. The remainder is
 * breathing room above the scrollport's bottom edge.
 */
const PINNED_PANEL_GUTTER_PX = 24;

/** Stable no-op so the embedded GoalsTab's `onClose` prop keeps a stable identity. */
const noop = () => {};

interface OASISFormProps {
  visitId: string;
  visitType: VisitTypeId;
  /** From API `VisitResponse.is_oasis` / `VISIT_TYPE_INFO.is_oasis` — never infer from `visitType` string shape. */
  isOasis: boolean;
  initialData?: OASISFormData;
  onSave: (data: OASISFormData) => void | Promise<void>;
  hasActiveSignatures?: boolean;
  showInlineValidation?: boolean;
  /**
   * Reports autosave status changes so the parent can render an indicator.
   * changeToken increments on every edit that resets the debounce timer.
   */
  onSaveStatusChange?: (status: AutoSaveStatus, changeToken: number) => void;
  /** Patient the visit belongs to; used by the embedded wounds and meds modules. */
  patientId: string;
  /**
   * Full patient record; used by the embedded allergies module. Optional so the
   * form still renders while the parent is loading it; the allergies section is
   * withheld until it is available.
   */
  patient?: PatientResponse | null;
  /** Current visit status; gates editing in the embedded modules. */
  visitStatus: TaskStatus;
  /** True when the visit's period is closed; the embedded modules lock too (EMR-1859). */
  episodeClosed?: boolean;
  /** Episode the visit belongs to; scopes the embedded meds module's orders. */
  episodeId?: string;
  /** Episode window; used by the embedded goals section. */
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /** Forwarded to the embedded meds module when a medication order is created. */
  onMedicationOrderCreated?: (order: OrderResponse) => void;
  /**
   * Offer "copy from a previous visit" (EMR-675, EMR-1008). Only routine visits
   * on a carryover-enabled form, where every source shares this visit's form
   * schema.
   */
  carryoverEnabled?: boolean;
  /**
   * Fires whenever a copy starts or finishes awaiting a decision (EMR-675).
   * While one is pending the copied answers are on screen but deliberately not
   * autosaved, so the surrounding panel must know to keep sign/submit — which
   * act on saved data — from committing the older answers.
   */
  onCarryoverPendingChange?: (pending: boolean) => void;
  /** Clinic the visit belongs to; scopes the attending physician picker. */
  agencyClinicId?: string | null;
  /**
   * Reports the Live Scrubber's fatal count after each validation, so the
   * panel can stop a signature the agency requires a clean chart for.
   */
  onFatalCountChange?: (count: number) => void;
  /**
   * Bump to open the Live Scrubber on the All CMS tab and scroll it into view.
   * 0 (the default) never opens it.
   */
  scrubberFocusRequest?: number;
}

const OASIS_SCRUBBER_HIGHLIGHT_CLASSES = [
  'ring-2',
  'ring-primary',
  'ring-offset-2',
  'rounded-md',
  'transition',
];
const OASIS_SCRUBBER_HIGHLIGHT_MS = 3000;

interface ScrubberNavigationTarget {
  qid: string | null;
  element: HTMLElement;
}

interface PendingScrubberNavigation {
  qids: string[];
  attemptedCategories: string[];
}

interface PendingContradictionNavigation {
  location: ContradictionFieldRef;
}

const findVisibleOasisNavigationTarget = (
  qids: string[]
): ScrubberNavigationTarget | null => {
  for (const qid of qids) {
    const classicTarget = document.getElementById(`oasis-q-${qid}`);
    if (
      classicTarget instanceof HTMLElement &&
      isVisibleOasisNavigationTarget(classicTarget)
    ) {
      return { qid, element: classicTarget };
    }

    const safeQid = escapeCssAttributeValue(qid);
    const bespokeTargets = document.querySelectorAll<HTMLElement>(
      `[data-qid="${safeQid}"]`
    );
    for (const bespokeTarget of bespokeTargets) {
      if (isVisibleOasisNavigationTarget(bespokeTarget)) {
        return { qid, element: bespokeTarget };
      }
    }
  }

  return null;
};

const getNextUnattemptedCategory = (
  qids: string[],
  qidToCategoryMap: Record<string, string>,
  attemptedCategories: string[]
) => {
  const attempted = new Set(attemptedCategories);
  for (const qid of qids) {
    const category = qidToCategoryMap[qid];
    if (category && !attempted.has(category)) return category;
  }
  return null;
};

const addOasisNavigationHighlight = (element: HTMLElement) => {
  element.classList.add(...OASIS_SCRUBBER_HIGHLIGHT_CLASSES);
};

const removeOasisNavigationHighlight = (element: HTMLElement) => {
  element.classList.remove(...OASIS_SCRUBBER_HIGHLIGHT_CLASSES);
};

/** A flattened grid item - either a standalone question or context about its parent */
interface GridItem {
  question: Question;
  span: GridSpan;
}

const getDirectSubitemCount = (question: Question): number =>
  Object.values(question.subitems ?? {}).reduce(
    (sum, subitems) => sum + subitems.length,
    0
  );

/**
 * Resolve container span for subgroup parents:
 * - schema width wins
 * - otherwise use a compact default based on how many direct subitems exist
 */
const resolveSubgroupContainerSpan = (question: Question): GridSpan => {
  if (
    question.roger_emr_display_width !== undefined &&
    question.roger_emr_display_width >= 1 &&
    question.roger_emr_display_width <= 12
  ) {
    return question.roger_emr_display_width as GridSpan;
  }

  const directSubitems = getDirectSubitemCount(question);
  if (directSubitems <= 1) return 3;
  if (directSubitems <= 2) return 6;
  if (directSubitems <= 4) return 9;
  return 12;
};

/**
 * Maps each question to a grid item, sizing its span by whether it has subitems.
 */
const flattenQuestions = (questions: Question[]): GridItem[] => {
  return questions.map(question => {
    if (question.subitems && Object.keys(question.subitems).length > 0) {
      return { question, span: resolveSubgroupContainerSpan(question) };
    }
    return { question, span: resolveQuestionSpan(question) };
  });
};

const buildQidToCategoryMap = (schema: OASISSchema | null) => {
  const map: Record<string, string> = {};
  if (!schema) return map;

  const collectQuestion = (question: Question, category: string) => {
    if (question.qid) {
      map[question.qid] = category;
    }
    if (!question.subitems) return;
    Object.values(question.subitems).forEach(subitems => {
      subitems.forEach(subitem => collectQuestion(subitem, category));
    });
  };

  Object.entries(schema).forEach(([category, questions]) => {
    questions.forEach(question => collectQuestion(question, category));
  });

  return map;
};

const buildQidToQuestionText = (schema: OASISSchema | null) => {
  const map: Record<string, string> = {};
  if (!schema) return map;

  const collect = (question: Question) => {
    if (question.qid && question.question) {
      map[question.qid] = question.question;
    }
    if (!question.subitems) return;
    Object.values(question.subitems).forEach(subitems => {
      subitems.forEach(sub => collect(sub));
    });
  };

  Object.values(schema).forEach(questions => {
    questions.forEach(q => collect(q));
  });

  return map;
};

/**
 * Hold the dense 12-column question grid at a usable width without forcing
 * embedded modules (Goals, Meds, Wounds, Diagnoses) onto the same floor.
 * Horizontal overflow stays inside this pocket so right-aligned actions on
 * those modules remain reachable on iPad-width viewports (support 29988).
 * Below `md` fields stack full-width, so no floor.
 */
function QuestionGridFloor({ children }: { children: React.ReactNode }) {
  return (
    <div className='overflow-x-auto'>
      <div className='md:min-w-[64rem]'>{children}</div>
    </div>
  );
}

const OASISFormComponent: React.FC<OASISFormProps> = ({
  visitId,
  visitType,
  isOasis,
  initialData,
  onSave,
  hasActiveSignatures = false,
  showInlineValidation = false,
  onSaveStatusChange,
  onFatalCountChange,
  scrubberFocusRequest = 0,
  patientId,
  patient,
  visitStatus,
  episodeClosed,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  onMedicationOrderCreated,
  carryoverEnabled = false,
  onCarryoverPendingChange,
  agencyClinicId,
}) => {
  const {
    schema,
    formData,
    currentCategory,
    isLoading,
    error,
    schemaVersion,
    setSchema,
    setFormData,
    setCurrentCategory,
    setIsLoading,
    setError,
    setLastSaved,
    setSchemaVersion,
  } = useOASISFormStore();

  const { hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const useVersionedFormSchemas = true;
  // QA findings only exist for flagged agencies, so everyone else would get a
  // permanently empty QA tab (EMR-809 review).
  const qaTabEnabled = hasFeatureFlag(FeatureFlag.CONTRADICTION_RULES_ENABLED);

  const [categories, setCategories] = useState<string[]>([]);
  // `section_id → display_name` from the active FSV's section_order. Empty
  // map for FSVs that don't ship one yet, in which case `resolveCategoryLabel`
  // falls back to the legacy regex transform.
  const labelByCategory = useMemo(
    () => buildLabelByCategory(schemaVersion),
    [schemaVersion]
  );
  /** Default (null) durations latch outcomes until the next edit; the pill renders them directly. */
  const { saveStatus, changeToken, reportEdit, beginSave, resolveSave } =
    useSaveStatus();
  /**
   * The answers as they stood before a carryover copy was applied (EMR-675),
   * or null when no copy is awaiting a decision.
   *
   * A copy writes straight into `formData` — there is exactly one store of
   * answers, so editing, clearing, and validating a copied answer are ordinary
   * form operations with no second map to reconcile. What makes the copy
   * reversible is this snapshot, not a separate copy of the answers: Discard is
   * "put these back", and it restores the clinician's own pre-copy edits too.
   *
   * Non-null is also the single "a copy is pending" signal: it holds autosave
   * back, keeps the banner up, and blocks sign/submit in the parent.
   */
  const [preCarryoverAnswers, setPreCarryoverAnswers] =
    useState<OASISFormData | null>(null);
  const [carryoverSourceLabel, setCarryoverSourceLabel] = useState<
    string | null
  >(null);
  /** Qids a pending copy wrote, for the "copied" marker on each field. Display only. */
  const [carryoverQids, setCarryoverQids] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const carryoverPending = preCarryoverAnswers !== null;
  useEffect(() => {
    onCarryoverPendingChange?.(carryoverPending);
  }, [carryoverPending, onCarryoverPendingChange]);
  /** Per-form DB bespoke template map (schema version → section → entry). */
  const externalAnswers = useVisitExternalAnswers(visitId);

  const [bespokeDbConfig, setBespokeDbConfig] = useState<BespokeDbConfig>(
    () => new Map()
  );
  const { error: showError } = useToast();
  const [inlineValidationErrors, setInlineValidationErrors] = useState<
    OasisValidationError[]
  >([]);
  const [inlineContradictions, setInlineContradictions] = useState<
    ContradictionFinding[]
  >([]);
  const [inlineValidationLoading, setInlineValidationLoading] = useState(false);
  const [inlineValidationErrorMessage, setInlineValidationErrorMessage] =
    useState<string | null>(null);
  const [hasValidatedOnce, setHasValidatedOnce] = useState(false);
  const [isScrubberExpanded, setIsScrubberExpanded] = useState(false);
  // Mobile-only collapsible section picker (replaces the desktop side nav)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  // Close the picker whenever the section changes, including navigation
  // driven outside the picker (e.g. Live Scrubber jump-to-question).
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [currentCategory]);
  // Shared across sections by design — switching section keeps the chosen tab.
  const [scrubberTab, setScrubberTab] = useState<ScrubberTab>('section');
  const [highlightedQid, setHighlightedQid] = useState<string | null>(null);
  const [pendingScrubberNavigation, setPendingScrubberNavigation] =
    useState<PendingScrubberNavigation | null>(null);
  const [pendingContradictionNavigation, setPendingContradictionNavigation] =
    useState<PendingContradictionNavigation | null>(null);
  const hasRunInitialValidation = useRef(false);
  /** Bumped per scrubber request, so a late response from an older one is dropped. */
  const latestValidationRequestRef = useRef(0);

  /** Avoid resetting local edits when parent `initialData` updates after autosave. */
  const hydratedVisitIdRef = useRef<string | null>(null);

  /** The pinned Live Scrubber, which jump targets must land clear of. */
  const scrubberPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showInlineValidation || !isOasis || !hasValidatedOnce) return;
    onFatalCountChange?.(
      inlineValidationErrors.filter(err => err.severity === 'fatal').length
    );
  }, [
    showInlineValidation,
    isOasis,
    hasValidatedOnce,
    inlineValidationErrors,
    onFatalCountChange,
  ]);

  useEffect(() => {
    if (!scrubberFocusRequest) return;
    setScrubberTab('all');
    setIsScrubberExpanded(true);
    scrubberPanelRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [scrubberFocusRequest]);

  const qidToCategoryMap = useMemo(
    () => buildQidToCategoryMap(schema),
    [schema]
  );
  const qidToQuestionText = useMemo(
    () => buildQidToQuestionText(schema),
    [schema]
  );
  // Sidebar dots follow the active tab: the CMS tabs and the QA tab are read by
  // different people, so each marks only its own audience's sections (EMR-809).
  const fatalCountByCategory = useMemo(
    () =>
      scrubberTab === 'qa'
        ? {}
        : countFatalsByCategory(inlineValidationErrors, qidToCategoryMap),
    [scrubberTab, inlineValidationErrors, qidToCategoryMap]
  );
  const advisoryCountByCategory = useMemo(
    () =>
      scrubberTab === 'qa'
        ? countQaFindingsByCategory(inlineContradictions, qidToCategoryMap)
        : countCmsWarningsByCategory(inlineValidationErrors, qidToCategoryMap),
    [
      scrubberTab,
      inlineValidationErrors,
      inlineContradictions,
      qidToCategoryMap,
    ]
  );

  /**
   * Deferred work owned by the current highlight. A jump outlives its own call
   * by up to OASIS_SCRUBBER_HIGHLIGHT_MS, so the next jump has to supersede it
   * and an unmount has to cancel it - a timer that fires after the tree is gone
   * sets state on nothing (EMR-1766).
   */
  const highlightFrameRef = useRef<number | null>(null);
  const highlightTimeoutsRef = useRef<number[]>([]);
  const highlightedElementsRef = useRef<HTMLElement[]>([]);

  const clearPendingHighlight = useCallback(() => {
    if (highlightFrameRef.current !== null) {
      window.cancelAnimationFrame(highlightFrameRef.current);
      highlightFrameRef.current = null;
    }
    for (const timeoutId of highlightTimeoutsRef.current) {
      window.clearTimeout(timeoutId);
    }
    highlightTimeoutsRef.current = [];
    for (const element of highlightedElementsRef.current) {
      removeOasisNavigationHighlight(element);
    }
    highlightedElementsRef.current = [];
  }, []);

  useEffect(() => clearPendingHighlight, [clearPendingHighlight]);

  /** Highlight every target; scroll to the first. */
  const navigateToResolvedTargets = useCallback(
    (targets: ScrubberNavigationTarget[]) => {
      if (targets.length === 0) return;
      clearPendingHighlight();
      const [scrollTarget] = targets;
      setHighlightedQid(scrollTarget.qid);
      // A cross-section jump resolves its target in the same commit that swaps
      // the section's children, and a smooth scroll requested mid-swap is
      // dropped. A frame puts it after that commit.
      highlightFrameRef.current = window.requestAnimationFrame(() => {
        highlightFrameRef.current = null;
        if (!scrollTarget.element.isConnected) return;
        centerInScrollPort(
          scrollTarget.element,
          scrubberPanelRef.current?.getBoundingClientRect().height ?? 0
        );
      });
      highlightTimeoutsRef.current.push(
        window.setTimeout(() => {
          for (const target of targets) {
            addOasisNavigationHighlight(target.element);
          }
          highlightedElementsRef.current = targets.map(
            target => target.element
          );
        }, 0)
      );
      highlightTimeoutsRef.current.push(
        window.setTimeout(() => {
          clearPendingHighlight();
          setHighlightedQid(null);
        }, OASIS_SCRUBBER_HIGHLIGHT_MS)
      );
    },
    [clearPendingHighlight]
  );

  const navigateToResolvedTarget = useCallback(
    (target: ScrubberNavigationTarget) => navigateToResolvedTargets([target]),
    [navigateToResolvedTargets]
  );

  const navigateToQids = useCallback(
    (qids: string[]) => {
      const uniqueQids = Array.from(new Set(qids.filter(Boolean)));
      if (uniqueQids.length === 0) return false;

      const target = findVisibleOasisNavigationTarget(uniqueQids);
      if (target) {
        setPendingScrubberNavigation(null);
        navigateToResolvedTarget(target);
        return true;
      }

      const attemptedCategories = currentCategory ? [currentCategory] : [];
      const nextCategory = getNextUnattemptedCategory(
        uniqueQids,
        qidToCategoryMap,
        attemptedCategories
      );

      if (!nextCategory) {
        setPendingScrubberNavigation(null);
        return false;
      }

      setPendingScrubberNavigation({
        qids: uniqueQids,
        attemptedCategories: [...attemptedCategories, nextCategory],
      });
      setCurrentCategory(nextCategory);
      return true;
    },
    [
      qidToCategoryMap,
      currentCategory,
      setCurrentCategory,
      navigateToResolvedTarget,
    ]
  );

  const navigateToContradictionLocation = useCallback(
    (location: ContradictionFieldRef) => {
      if (location.qids?.length && navigateToQids(location.qids)) {
        return;
      }

      const sourceTargets = findContradictionSourceTargets(location);
      if (sourceTargets.length > 0) {
        setPendingContradictionNavigation(null);
        navigateToResolvedTargets(
          sourceTargets.map(element => ({ qid: null, element }))
        );
        return;
      }

      // Embedded modules (active dx / wounds / meds) only mount on their
      // section — switch there and let the pending-nav effect wait for the
      // data-contradiction-source anchor.
      // Also pending when we are already on the section: the module may still
      // be fetching the rows this location names.
      const embeddedSection = sectionIdForContradictionSource(location.source);
      if (embeddedSection) {
        setPendingContradictionNavigation({ location });
        if (currentCategory !== embeddedSection) {
          setCurrentCategory(embeddedSection);
        }
      }
    },
    [
      navigateToQids,
      navigateToResolvedTargets,
      currentCategory,
      setCurrentCategory,
    ]
  );

  const navigateToError = useCallback(
    (err: OasisValidationError) => {
      if (navigateToQids(err.qids || [])) return;
      // Diagnosis severity errors name a diagnosis row, which has no qid to jump
      // to. The Active Diagnosis module is the same embedded target the QA path
      // resolves for `diagnosis_list`, so reuse that navigation -- including its
      // wait for the module to mount and finish fetching (EMR-1280).
      if (cmsErrorIsDiagnosisSeverity(err)) {
        navigateToContradictionLocation({
          source: 'diagnosis_list',
          qids: [],
          label: 'Active Diagnosis',
        });
      }
    },
    [navigateToQids, navigateToContradictionLocation]
  );

  useLayoutEffect(() => {
    if (!pendingScrubberNavigation) return;

    const target = findVisibleOasisNavigationTarget(
      pendingScrubberNavigation.qids
    );
    if (target) {
      navigateToResolvedTarget(target);
      setPendingScrubberNavigation(null);
      return;
    }

    const nextCategory = getNextUnattemptedCategory(
      pendingScrubberNavigation.qids,
      qidToCategoryMap,
      pendingScrubberNavigation.attemptedCategories
    );
    if (!nextCategory) {
      setPendingScrubberNavigation(null);
      return;
    }

    setPendingScrubberNavigation({
      qids: pendingScrubberNavigation.qids,
      attemptedCategories: [
        ...pendingScrubberNavigation.attemptedCategories,
        nextCategory,
      ],
    });
    setCurrentCategory(nextCategory);
  }, [
    pendingScrubberNavigation,
    qidToCategoryMap,
    setCurrentCategory,
    navigateToResolvedTarget,
  ]);

  useEffect(() => {
    if (!pendingContradictionNavigation) return;
    const { location } = pendingContradictionNavigation;

    // Embedded module anchors mount after the section switch (and Active
    // Diagnosis may still be spinning). Retry until the
    // data-contradiction-source node appears or we time out.
    const tryResolve = () => {
      const sourceTargets = findContradictionSourceTargets(location);
      if (sourceTargets.length === 0) return false;
      navigateToResolvedTargets(
        sourceTargets.map(element => ({ qid: null, element }))
      );
      setPendingContradictionNavigation(null);
      return true;
    };

    if (tryResolve()) return;

    const intervalId = window.setInterval(() => {
      if (tryResolve()) {
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
      }
    }, 50);
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      setPendingContradictionNavigation(null);
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [
    pendingContradictionNavigation,
    currentCategory,
    navigateToResolvedTargets,
  ]);

  /**
   * Persist `data`, reporting whether it actually reached the server.
   *
   * Autosave callers ignore the result — a failed autosave retries on the next
   * edit. Carryover's Keep does not: it has to know, because until the save
   * lands the copied answers exist only in this component.
   */
  const saveFormData = async (
    data: OASISFormData
  ): Promise<'saved' | 'declined' | 'error'> => {
    const saveToken = beginSave();
    try {
      const dataToSave = schema ? getOASISFormData(data, schema) : data;
      await onSave(dataToSave);
      setLastSaved(new Date());
      resolveSave(saveToken, 'saved');
      return 'saved';
    } catch (error) {
      if (error instanceof SaveDeclinedError) {
        // The user declined the signature-invalidation dialog. The edits
        // are still unsaved (the parent discards them by remounting this
        // form), but nothing failed, so don't report an error.
        resolveSave(saveToken, 'declined');
        return 'declined';
      }
      console.error('Auto-save failed:', error);
      resolveSave(saveToken, 'error');
      return 'error';
    }
  };

  // Debounced save function (save after 2 seconds of inactivity, or immediately if there are signatures)
  const debouncedSave = useDebounce((data: OASISFormData) => {
    void saveFormData(data);
  }, getOasisAutosaveDebounceMs(hasActiveSignatures));

  useEffect(() => {
    onSaveStatusChange?.(saveStatus, changeToken);
  }, [saveStatus, changeToken, onSaveStatusChange]);

  /**
   * Per-rule dismissal state this client knows better than a given validation
   * response. Two cases, and only these two:
   *   - `pending`: a dismiss/restore is in flight, so the user's intent is not
   *     on the server yet. Its desired value wins over any response.
   *   - `settled`: a mutation has landed, tagged with the version it landed at.
   *     A validation request that started *before* that version was answered
   *     from pre-mutation state, so its answer for that rule is stale.
   * A validation response that started after a rule's last mutation is
   * authoritative for it — that is what lets another tab's change through
   * instead of pinning the old value until reload.
   */
  const pendingDismissalsRef = useRef(new Map<string, boolean>());
  const settledDismissalsRef = useRef(
    new Map<string, { dismissed: boolean; version: number }>()
  );
  const dismissalVersionRef = useRef(0);

  const applyKnownDismissals = useCallback(
    (findings: ContradictionFinding[]): ContradictionFinding[] =>
      findings.map(finding => {
        const pending = pendingDismissalsRef.current.get(finding.rule_id);
        const settled = settledDismissalsRef.current.get(finding.rule_id);
        const known = pending ?? settled?.dismissed;
        if (known === undefined || finding.dismissed === known) return finding;
        return { ...finding, dismissed: known };
      }),
    []
  );

  /**
   * Forget every settled mutation a validation request already saw. Its answer
   * for those rules is at least as new as ours, so keeping them would let an
   * old click override the server again the next time anything reconciles —
   * which is how dismissing one finding could re-hide an unrelated one that had
   * since been restored elsewhere.
   */
  const forgetDismissalsSeenBy = useCallback((requestedAtVersion: number) => {
    for (const [ruleId, entry] of settledDismissalsRef.current) {
      if (entry.version <= requestedAtVersion) {
        settledDismissalsRef.current.delete(ruleId);
      }
    }
  }, []);

  const runInlineValidation = useCallback(
    async (data: OASISFormData) => {
      // `showInlineValidation` says this surface wants the scrubber; `isOasis`
      // says the visit qualifies for one. Without both, a non-OASIS visit POSTs
      // /validateOasis and gets a 400.
      if (!showInlineValidation || !isOasis || !visitId || !schema) return;

      setInlineValidationLoading(true);
      setInlineValidationErrorMessage(null);
      const requestedAtVersion = dismissalVersionRef.current;
      const requestId = ++latestValidationRequestRef.current;
      const isStale = () => requestId !== latestValidationRequestRef.current;

      try {
        const response = await validateOasisVisitsVisitIdValidateOasisPost({
          path: { visit_id: visitId },
          body: {
            answers: getOASISFormData(data, schema) as Record<string, unknown>,
          },
        });
        if (isStale()) return;

        if (response.error) {
          setInlineValidationErrorMessage(
            getUserFacingApiMessage(
              response,
              'Validation failed. Please try again.'
            )
          );
          setInlineValidationErrors([]);
          setInlineContradictions([]);
        } else {
          setInlineValidationErrors(response.data?.errors || []);
          forgetDismissalsSeenBy(requestedAtVersion);
          setInlineContradictions(
            applyKnownDismissals(response.data?.contradictions || [])
          );
        }
      } catch (err) {
        console.error('Inline validation failed:', err);
        if (isStale()) return;
        setInlineValidationErrorMessage('Validation failed. Please try again.');
        setInlineValidationErrors([]);
        setInlineContradictions([]);
      } finally {
        if (!isStale()) {
          setInlineValidationLoading(false);
          setHasValidatedOnce(true);
        }
      }
    },
    [
      applyKnownDismissals,
      forgetDismissalsSeenBy,
      isOasis,
      schema,
      showInlineValidation,
      visitId,
    ]
  );

  /**
   * Dismiss or restore one QA finding (EMR-1145). Flips the local flag first so
   * the list and counts move immediately, and puts it back if the call fails —
   * a re-validation would otherwise be needed to see the result of a click.
   */
  const setContradictionDismissed = useCallback(
    async (ruleId: string, dismissed: boolean) => {
      if (!visitId) return;

      const applyDismissed = (value: boolean) =>
        setInlineContradictions(current =>
          current.map(finding =>
            finding.rule_id === ruleId
              ? { ...finding, dismissed: value }
              : finding
          )
        );

      pendingDismissalsRef.current.set(ruleId, dismissed);
      applyDismissed(dismissed);
      try {
        const call = dismissed
          ? dismissQaScrubberItemVisitsVisitIdQaScrubberRuleIdDismissPost
          : restoreQaScrubberItemVisitsVisitIdQaScrubberRuleIdDismissDelete;
        const response = await call({
          path: { visit_id: visitId, rule_id: ruleId },
        });
        if (response.error) throw response.error;
        // Reconcile against the visit's full dismissed list rather than trusting
        // the optimistic flip: this is why the endpoints return the whole set.
        // Recorded per rule, not as a whole-set replacement — several findings
        // can be dismissed at once, and a slower response must not stamp its
        // own snapshot over a rule another call already settled.
        const serverDismissed = new Set(
          response.data?.dismissed_rule_ids || []
        );
        const version = ++dismissalVersionRef.current;
        pendingDismissalsRef.current.delete(ruleId);
        settledDismissalsRef.current.set(ruleId, {
          dismissed: serverDismissed.has(ruleId),
          version,
        });
        setInlineContradictions(current => applyKnownDismissals(current));
      } catch (err) {
        console.error('QA scrubber dismissal failed:', err);
        pendingDismissalsRef.current.delete(ruleId);
        applyDismissed(!dismissed);
        showError(
          dismissed ? 'Failed to dismiss item' : 'Failed to restore item'
        );
      }
    },
    [applyKnownDismissals, showError, visitId]
  );

  const debouncedInlineValidation = useDebounce((data: OASISFormData) => {
    void runInlineValidation(data);
  }, OASIS_INLINE_VALIDATION_DEBOUNCE_MS);

  // Contradiction findings are built from a server-side snapshot that reads
  // diagnoses, wounds, and medications straight from the database
  // (`build_contradiction_snapshot`), so edits made in those embedded modules
  // change scrubber output without touching a single OASIS answer. Re-validate
  // on each of them or the Live Scrubber keeps showing the stale snapshot until
  // the next OASIS edit or reload.
  const handleScrubberInputChanged = useCallback(() => {
    if (!showInlineValidation) return;
    debouncedInlineValidation(useOASISFormStore.getState().formData);
  }, [debouncedInlineValidation, showInlineValidation]);

  const scrubberExpandedBodyRef = useRef<HTMLDivElement>(null);
  const scrubberStableBodyHeightRef = useRef(0);

  useLayoutEffect(() => {
    if (!showInlineValidation || !isOasis) return;
    if (!isScrubberExpanded) {
      scrubberStableBodyHeightRef.current = 0;
      return;
    }
    if (inlineValidationLoading) return;
    const el = scrubberExpandedBodyRef.current;
    if (!el) return;
    scrubberStableBodyHeightRef.current = el.offsetHeight;
  }, [
    showInlineValidation,
    isOasis,
    isScrubberExpanded,
    inlineValidationLoading,
    inlineValidationErrors,
    inlineValidationErrorMessage,
    hasValidatedOnce,
    scrubberTab,
    currentCategory,
  ]);

  useEffect(() => {
    setCurrentCategory(null);
  }, [visitType, setCurrentCategory]);

  useEffect(() => {
    const fetchSchema = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Always assigned: `useVersionedFormSchemas` is constant `true`, so the
        // versioned branch below always runs (definite-assignment assertion).
        let schemaData!: OASISSchema;
        // Captured from the versioned response so `categoryList` below can
        // honor `section_order` without round-tripping through Zustand
        // (state setters are async, the local `schemaVersion` closed over
        // at render time is stale here).
        let fetchedVersion: FormSchemaVersion | null = null;

        if (useVersionedFormSchemas) {
          const response = await getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet(
            {
              path: { visit_id: visitId },
            }
          );

          if (!response?.data) {
            throw new Error(
              'Could not load the required form schema. Please contact support.'
            );
          }

          const { version, schema_data } = response.data as {
            version: FormSchemaVersion;
            schema_data: OASISSchema;
          };

          schemaData = schema_data;
          fetchedVersion = version;
          setSchemaVersion(version);

          const bespokePin = pinFromFormSchemaVersion(
            version.form_type as RogerDocFormType,
            version
          );
          if (!bespokePin) {
            throw new Error(
              'The visit schema is missing form type or version metadata required to load customized section layouts. Please contact support.'
            );
          }

          const bespokeRes =
            await listEnabledSectionsBespokeTemplatesEnabledGet({
              query: {
                form_type: bespokePin.form_type,
                schema_version_name: bespokePin.schema_version_name,
                form_schema_version: bespokePin.form_schema_version,
              },
            });
          const entries =
            (bespokeRes?.data as EnabledBespokeEntry[] | undefined) ?? [];
          const sectionMap = new Map(
            entries.map(e => [
              e.section_name,
              {
                section_name: e.section_name,
                layout_json:
                  (e.layout_json as
                    import('./bespoke/layout/types').LayoutNode[] | null) ??
                  null,
              },
            ])
          );
          setBespokeDbConfig(() => new Map([[version.name, sectionMap]]));
        }

        setSchema(schemaData);

        // Prefer the FormSchemaVersion's `section_order` for both the section list
        // and its order (ROG-2295).
        //
        //   1. Filter `section_order` to keys that actually exist in
        //      `schemaData` — an FSV that lists a section the build response
        //      doesn't populate must not produce an empty tab.
        //   2. Append any `schemaData` keys the FormSchemaVersion's `section_order`
        //      omits — in case we have typos or missed a section in section_order.
        const categoryList = (fetchedVersion?.section_order ?? [])
          .map(item => item.section_id)
          .filter(sectionId => sectionId in schemaData);
        for (const key of Object.keys(schemaData)) {
          if (!categoryList.includes(key)) categoryList.push(key);
        }
        if (supportsActiveDiagnosisPage(visitType)) {
          categoryList.push(ACTIVE_DIAGNOSIS_CATEGORY);
        }
        // A visit gets a Goals page when it can create goals or record progress
        // against them. Transfer and death notes can do neither.
        if (isOasis && showsGoalsSection(visitType)) {
          categoryList.push(GOALS_CATEGORY);
        }
        // Falls and infections get their own page here for the same reason
        // goals do: an OASIS form is paginated, so there is no bottom of the
        // form to hang them off. No OASIS visit type is in the events list
        // today, which leaves this dormant until one is added.
        if (isOasis && showsEventsSection(visitType)) {
          categoryList.push(EVENTS_CATEGORY);
        }
        setCategories(categoryList);
        if (categoryList.length > 0) {
          setCurrentCategory(categoryList[0]);
        }
      } catch (err) {
        console.error('Failed to fetch question schema:', err);
        setError('Failed to load form schema. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchema();
  }, [
    visitId,
    visitType,
    useVersionedFormSchemas,
    setSchema,
    setIsLoading,
    setError,
    setCurrentCategory,
    setSchemaVersion,
    setCategories,
    setBespokeDbConfig,
    isOasis,
  ]);

  // A pending copy is per-visit; dropping it on visit navigation keeps it from
  // bleeding through the (un-keyed) global store.
  useEffect(() => {
    setPreCarryoverAnswers(null);
    setCarryoverSourceLabel(null);
    setCarryoverQids(new Set());
  }, [visitId]);

  // Reload/close warning. In-app route changes are NOT blocked: react-router
  // `useBlocker` needs a data router and this app does not use one.
  useEffect(() => {
    if (!carryoverPending) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [carryoverPending]);

  useEffect(() => {
    hydratedVisitIdRef.current = null;
    hasRunInitialValidation.current = false;
    // Seed answers immediately on visit navigation so the UI is not blank while
    // schema/bespoke layouts load. `initialData` is intentionally omitted from
    // deps — this runs once per visitId (OASISForm remounts via key= anyway).
    setFormData(initialData ?? {});
  }, [visitId, setFormData]);

  useEffect(() => {
    if (!schema || isLoading) return;

    // Versioned visits load bespoke layouts in a separate React state update
    // from schema (Zustand). Wait until fetchSchema has populated bespokeDbConfig.
    if (
      useVersionedFormSchemas &&
      schemaVersion?.name &&
      bespokeDbConfig.size === 0
    ) {
      return;
    }

    if (hydratedVisitIdRef.current !== visitId) {
      hydratedVisitIdRef.current = visitId;

      setFormData(initialData || {});

      if (showInlineValidation) {
        hasRunInitialValidation.current = true;
        void runInlineValidation(initialData || {});
      }
      return;
    }

    if (showInlineValidation && !hasRunInitialValidation.current) {
      hasRunInitialValidation.current = true;
      void runInlineValidation(useOASISFormStore.getState().formData);
    }
    /* `initialData` is omitted on purpose: autosave updates `visit.visit_data` while the user may have newer edits; re-hydrating would flash stale values. */
  }, [
    visitId,
    schema,
    isLoading,
    bespokeDbConfig,
    useVersionedFormSchemas,
    schemaVersion?.name,
    setFormData,
    showInlineValidation,
    runInlineValidation,
  ]);

  /**
   * Room available to the pinned section list and Live Scrubber. Both pin to the
   * top of whichever ancestor scrolls, and anything taller than that ancestor
   * can never be scrolled into view, so both cap themselves against this and
   * scroll internally instead.
   */
  const {
    ref: scrollportRef,
    height: scrollportHeight,
    scrollToTop,
  } = useScrollportHeight(PINNED_PANEL_GUTTER_PX);

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
    // Now that the section list stays reachable from anywhere in the form, a
    // switch can be made from deep in the previous section, which would
    // otherwise open the next one already scrolled past its heading. Scrubber
    // jumps set the category directly instead, so they keep their own target.
    scrollToTop();
  };

  /**
   * Cap for the expanded issue list. Expanding is deliberate, but a pinned panel
   * must still leave the form the larger share of the screen, so the standing
   * ceiling becomes an upper bound rather than the actual height. Before the
   * first measurement lands the ceiling applies on its own.
   */
  const scrubberBodyMaxHeight = scrollportHeight
    ? Math.max(
        SCRUBBER_BODY_MIN_HEIGHT_PX,
        Math.min(
          SCRUBBER_BODY_MAX_HEIGHT_PX,
          Math.round(scrollportHeight * SCRUBBER_BODY_SCROLLPORT_SHARE)
        )
      )
    : SCRUBBER_BODY_MAX_HEIGHT_PX;

  const handleFormDataChange = (updates: Partial<OASISFormData>) => {
    const newFormData: OASISFormData = { ...formData };
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) {
        delete newFormData[key];
      } else {
        newFormData[key] = value;
      }
    });

    if (Object.keys(updates).length > 0) {
      setFormData(newFormData);
      reportEdit();
      if (carryoverPending) {
        // While a copy awaits Keep or Discard, nothing on this form autosaves.
        // The copied answers are in `formData` now, so an autosave would commit
        // them without the clinician deciding — and Discard could no longer
        // take them back. Keep and Discard are the only writes while the banner
        // is up, and both save explicitly.
        debouncedSave.cancel();
      } else {
        debouncedSave(newFormData);
      }
    }

    if (showInlineValidation) {
      debouncedInlineValidation(newFormData);
    }
  };

  /**
   * Apply a copy to the answers on screen. Does not save (EMR-675).
   *
   * The source wins outright across everything it governs: `copyableQids` is
   * cleared first, then the source's answers written over the top. A question
   * the source left blank therefore ends up blank here rather than keeping this
   * visit's older answer under a copied one. Questions outside that list —
   * vitals, narratives, time in/out — are not touched.
   */
  const handleCarryoverLoaded = useCallback(
    (
      answers: Record<string, unknown>,
      copyableQids: string[],
      label: string
    ) => {
      // Anything queued from typing just before the copy is already in
      // `formData`, so it is in the snapshot and in what Keep saves.
      debouncedSave.cancel();

      // Read the answers as they stand now, not as they stood when the
      // clinician picked the source: the preview is a round trip, and anything
      // answered while it was in flight would otherwise be overwritten by the
      // older snapshot — and be missing from `preCarryoverAnswers`, so Discard
      // could not bring it back either.
      const current = useOASISFormStore.getState().formData;

      const next: OASISFormData = { ...current };
      copyableQids.forEach(qid => delete next[qid]);
      Object.assign(next, answers as OASISFormData);

      setPreCarryoverAnswers(current);
      setCarryoverQids(new Set(Object.keys(answers)));
      setCarryoverSourceLabel(label);
      setFormData(next);
      reportEdit();
      if (showInlineValidation) debouncedInlineValidation(next);
    },
    [
      debouncedSave,
      setFormData,
      reportEdit,
      showInlineValidation,
      debouncedInlineValidation,
    ]
  );

  const clearCarryover = () => {
    setPreCarryoverAnswers(null);
    setCarryoverSourceLabel(null);
    setCarryoverQids(new Set());
  };

  /** Commit the copied answers in a single immediate save (not debounced). */
  const handleKeepStaged = async () => {
    if (!carryoverPending) return;

    debouncedSave.cancel();
    const outcome = await saveFormData(formData);

    // `saveFormData` swallows every failure — a declined signature-invalidation
    // dialog, and a network or server error alike. Neither reached the server,
    // so the copy stays pending: the banner stays up, Sign stays blocked, and
    // the clinician can retry Keep. Clearing it here would say "saved" about
    // answers that live only in this browser tab, and would let the visit be
    // signed with the older answers on the record.
    if (outcome !== 'saved') return;
    clearCarryover();
  };

  /** Put back the answers as they stood before the copy, and persist them. */
  const handleDiscardStaged = async () => {
    if (!preCarryoverAnswers) return;

    debouncedSave.cancel();
    const restored = preCarryoverAnswers;
    setFormData(restored);
    if (showInlineValidation) debouncedInlineValidation(restored);

    // Autosave was held back for the whole pending window, so the server may
    // still be missing edits the clinician made just before copying. Those are
    // in the snapshot, so saving it restores the record as well as the screen.
    //
    // Clear the copy only once that landed, for the same reason Keep does: a
    // failed save leaves the screen showing the restored answers while the
    // record still holds older ones. Dropping the banner there would re-enable
    // Sign over that gap, and the state would persist until some later edit
    // happened to autosave. The banner staying up is the retry.
    const outcome = await saveFormData(restored);
    if (outcome !== 'saved') return;
    clearCarryover();
  };

  // Build each tab's lists once; panel picks the active tab without refiltering.
  const scrubberListModel = useMemo(() => {
    if (!showInlineValidation || !isOasis) return null;

    const totalFatalCount = inlineValidationErrors.filter(
      err => err.severity === 'fatal'
    ).length;
    const totalWarningCount = inlineValidationErrors.filter(
      err => err.severity === 'warning'
    ).length;

    const fatalsByTab = {} as Record<ScrubberTab, OasisValidationError[]>;
    const advisoryByTab = {} as Record<ScrubberTab, ScrubberListItem[]>;
    const tabCounts = {} as Record<ScrubberTab, number>;
    for (const tab of SCRUBBER_TABS) {
      fatalsByTab[tab] = buildFatalScrubberErrors(
        inlineValidationErrors,
        tab,
        currentCategory,
        qidToCategoryMap
      );
      advisoryByTab[tab] = buildAdvisoryScrubberItems(
        inlineValidationErrors,
        inlineContradictions,
        tab,
        currentCategory,
        qidToCategoryMap
      );
      // Dismissed QA findings still render on the QA tab but must not count
      // toward the badge (EMR-1145), so this is not the list length.
      tabCounts[tab] =
        fatalsByTab[tab].length + countActiveScrubberItems(advisoryByTab[tab]);
    }

    return {
      totalFatalCount,
      totalWarningCount,
      fatalsByTab,
      advisoryByTab,
      tabCounts,
    };
  }, [
    showInlineValidation,
    isOasis,
    inlineValidationErrors,
    inlineContradictions,
    currentCategory,
    qidToCategoryMap,
  ]);

  /** Carryover affordance, or the staged-answers bar once answers are copied (EMR-675). */
  const renderCarryoverBar = () => {
    if (!carryoverEnabled) return null;

    if (!carryoverPending) {
      return (
        <div className='mb-3 flex justify-end'>
          <CarryoverSourceMenu
            visitId={visitId}
            onAnswersLoaded={handleCarryoverLoaded}
          />
        </div>
      );
    }

    return (
      <CarryoverStagedBanner
        count={carryoverQids.size}
        sourceLabel={carryoverSourceLabel}
        onKeep={() => void handleKeepStaged()}
        onDiscard={() => void handleDiscardStaged()}
      />
    );
  };

  const renderInlineValidationPanel = () => {
    if (!scrubberListModel) return null;

    const {
      totalFatalCount,
      totalWarningCount,
      fatalsByTab,
      advisoryByTab,
      tabCounts,
    } = scrubberListModel;
    const visibleFatals = fatalsByTab[scrubberTab];
    const visibleAdvisory = advisoryByTab[scrubberTab];

    const visibleTabs = SCRUBBER_TAB_DEFINITIONS.filter(
      definition => definition.id !== 'qa' || qaTabEnabled
    );
    const scrubberNoIssuesMessage =
      visibleTabs.find(definition => definition.id === scrubberTab)
        ?.emptyMessage ?? 'No issues';

    const hasVisibleBodyItems =
      visibleFatals.length > 0 || visibleAdvisory.length > 0;
    return (
      // Pinned to the top of the scrollport so the counts and the section
      // filter stay reachable while editing fields far down the form.
      // Collapsed is the default and costs about one row of height.
      //
      // The outer band is opaque because form fields scroll underneath it; the
      // card keeps its own translucent tint so the surface looks unchanged.
      //
      // Below `md` the page scroller is the app shell's `main`, which carries
      // its own padding, and a scroll container's padding insets where sticky
      // children may pin. Pinning that much higher and carrying a band of the
      // same height covers the strip fields would otherwise scroll through; the
      // matching negative margin keeps the resting layout where it was. From
      // `md` up the panel's scroll box has no padding, so none of that applies.
      <div
        ref={scrubberPanelRef}
        className='sticky -top-4 -mt-4 pt-4 md:top-0 md:mt-0 md:pt-0 z-20 bg-background pb-3'
      >
        <div className='rounded-md border bg-muted/20 p-3'>
          {/* Toggle is separate from section-filter buttons so keyboard events
            on Section/Misc/All do not also collapse the scrubber. */}
          <div className='w-full flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5'>
            <button
              type='button'
              onClick={() => setIsScrubberExpanded(prev => !prev)}
              className='flex items-center gap-2 text-sm min-w-0 text-left cursor-pointer'
              aria-expanded={isScrubberExpanded}
              aria-label='Toggle Live Scrubber'
            >
              <AlertTriangle className='h-4 w-4 text-amber-600' />
              <span className='font-medium'>Live Scrubber</span>
              {inlineValidationLoading ? (
                <span className='text-xs text-muted-foreground'>
                  Checking...
                </span>
              ) : inlineValidationErrorMessage ? (
                <span className='text-xs text-destructive'>
                  Validation unavailable
                </span>
              ) : (
                <span className='text-xs text-muted-foreground'>
                  Checks run automatically
                </span>
              )}
              {isScrubberExpanded ? (
                <ChevronUp className='h-4 w-4 text-muted-foreground' />
              ) : (
                <ChevronDown className='h-4 w-4 text-muted-foreground' />
              )}
            </button>
            <div className='flex flex-wrap items-center gap-2'>
              {/* Stays put once validation has run, even at zero issues — the
                empty-state copy is tab-scoped, so hiding the pills would leave
                no way to tell which tab it is describing (EMR-809 review). */}
              {hasValidatedOnce && (
                <div className='flex flex-wrap gap-0.5'>
                  {visibleTabs.map(({ id: tab, label }) => {
                    const count = tabCounts[tab];
                    return (
                      <button
                        key={tab}
                        type='button'
                        onClick={() => {
                          setScrubberTab(tab);
                          if (!isScrubberExpanded) setIsScrubberExpanded(true);
                        }}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                          scrubberTab === tab
                            ? 'bg-foreground/10 text-foreground'
                            : 'text-muted-foreground hover:text-foreground/70'
                        }`}
                      >
                        {label ??
                          resolveCategoryLabel(
                            currentCategory || '',
                            labelByCategory
                          )}
                        {count > 0 && (
                          <span className='ml-0.5 opacity-70'>({count})</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {(totalFatalCount > 0 || totalWarningCount > 0) && (
                <div className='flex items-center gap-2 text-xs'>
                  {totalFatalCount > 0 && (
                    <span className='rounded bg-destructive/10 px-2 py-0.5 text-destructive'>
                      {totalFatalCount} fatal
                    </span>
                  )}
                  {totalWarningCount > 0 && (
                    <span className='rounded bg-amber-100 px-2 py-0.5 text-amber-700'>
                      {totalWarningCount} warning
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          {isScrubberExpanded && (
            <div
              ref={scrubberExpandedBodyRef}
              className='mt-2 overflow-y-auto'
              style={{
                maxHeight: scrubberBodyMaxHeight,
                ...(inlineValidationLoading &&
                scrubberStableBodyHeightRef.current > 0
                  ? {
                      minHeight: Math.min(
                        scrubberStableBodyHeightRef.current,
                        scrubberBodyMaxHeight
                      ),
                    }
                  : {}),
              }}
            >
              {inlineValidationLoading ? (
                <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                  <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
                  Running live checks...
                </div>
              ) : inlineValidationErrorMessage ? (
                <p className='text-xs text-destructive'>
                  {inlineValidationErrorMessage}
                </p>
              ) : hasVisibleBodyItems ? (
                <div className='space-y-2'>
                  {visibleFatals.map((err, idx) => (
                    <CmsIssueCard
                      key={`fatal-${err.edit_id}-${idx}`}
                      error={err}
                      qidToQuestionText={qidToQuestionText}
                      onJump={navigateToError}
                    />
                  ))}
                  {visibleAdvisory.map((item, idx) =>
                    item.kind === 'cms' ? (
                      <CmsIssueCard
                        key={`warning-${item.error.edit_id}-${idx}`}
                        error={item.error}
                        qidToQuestionText={qidToQuestionText}
                        onJump={navigateToError}
                      />
                    ) : (
                      <ContradictionFindingCard
                        key={item.finding.rule_id}
                        finding={item.finding}
                        currentCategory={currentCategory}
                        qidToCategoryMap={qidToCategoryMap}
                        labelByCategory={labelByCategory}
                        onHighlightLocation={navigateToContradictionLocation}
                        onSetDismissed={setContradictionDismissed}
                      />
                    )
                  )}
                </div>
              ) : hasValidatedOnce ? (
                <div className='text-xs text-muted-foreground'>
                  {scrubberNoIssuesMessage}
                </div>
              ) : (
                <div className='text-xs text-muted-foreground'>
                  Running checks for loaded data.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const currentQuestions = useMemo(
    () => (currentCategory && schema ? schema[currentCategory] : []) || [],
    [currentCategory, schema]
  );

  const schemaQuestionOptions: SchemaQuestionOption[] = useMemo(() => {
    if (!schema) return [];
    const options: SchemaQuestionOption[] = [];
    const seen = new Set<string>();

    const collect = (questions: Question[], category: string) => {
      for (const q of questions) {
        if (!seen.has(q.qid)) {
          seen.add(q.qid);
          options.push({
            qid: q.qid,
            label: q.question || q.qid,
            category,
          });
        }
        if (q.subitems) {
          for (const children of Object.values(q.subitems)) {
            collect(children, category);
          }
        }
      }
    };

    for (const [category, questions] of Object.entries(schema)) {
      collect(questions, category);
    }
    return options;
  }, [schema]);

  const gridItems = useMemo(
    () => flattenQuestions(currentQuestions),
    [currentQuestions]
  );

  // Minimal visit shape for the embedded goals section. Built from primitives so
  // the memoized form does not take the whole (frequently-replaced) visit object
  // as a prop.
  const goalsVisit: GoalsTabVisit = useMemo(
    () => ({
      id: visitId,
      status: visitStatus,
      episode_closed: episodeClosed,
      visit_type: visitType,
      episode_id: episodeId,
      episode_start_date: episodeStartDate,
      episode_end_date: episodeEndDate,
    }),
    [
      visitId,
      visitStatus,
      episodeClosed,
      visitType,
      episodeId,
      episodeStartDate,
      episodeEndDate,
    ]
  );

  // Patient names are only shown in GoalsTab's dialog header, which inline mode
  // hides, so empty strings are safe here.
  const renderGoalsSection = () => (
    <GoalsTab
      open
      onClose={noop}
      patientFirstName=''
      patientLastName=''
      visit={goalsVisit}
      inline
      capabilities={visitGoalCapabilities(visitType)}
      discipline={getDisciplineForVisitType(visitType)}
    />
  );

  // The falls and infections module, plus the caveat WellSky also prints: these
  // are patient-level incident records, not answers on this note, so the
  // generated PDF does not carry them.
  const renderEventsSection = () => (
    <>
      <PatientEvents
        patientId={patientId}
        agencyClinicId={agencyClinicId}
        episodeId={episodeId}
        episodeStartDate={episodeStartDate}
        episodeEndDate={episodeEndDate}
      />
      <p className='mt-2 text-xs text-muted-foreground'>
        Fall and infection logs are not included in the printed note.
      </p>
    </>
  );

  // PatientMedications requires a handler; forward to the parent when provided.
  const handleMedicationOrderCreated = useCallback(
    (order: OrderResponse) => onMedicationOrderCreated?.(order),
    [onMedicationOrderCreated]
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return <p className='text-destructive text-sm py-4'>{error}</p>;
  }

  if (!schema || categories.length === 0) {
    return (
      <p className='text-muted-foreground text-sm py-4'>
        No form schema available for this visit type.
      </p>
    );
  }

  const renderGridItem = (item: GridItem) => {
    const spanClass = SPAN_CLASSES[item.span];

    return (
      <div key={item.question.qid} className={`${spanClass} min-w-0`}>
        <OASISQuestionRenderer
          question={item.question}
          value={formData[item.question.qid]}
          onChange={value => {
            const updates: Partial<OASISFormData> = {
              [item.question.qid]: value,
            };

            const clearUpdates = getSubitemClearUpdates(
              item.question,
              value,
              formData
            );
            Object.assign(updates, clearUpdates);

            handleFormDataChange(updates);
          }}
          formData={formData}
          containerSpan={item.span}
          onFormDataChange={handleFormDataChange}
          highlightedQid={highlightedQid}
          stagedQids={carryoverQids}
        />
      </div>
    );
  };

  // Wounds and meds are surfaced at the bottom of their matching OASIS sections
  // so clinicians can review them before submitting the chart.
  const renderEmbeddedSectionModules = () => {
    const category = currentCategory || '';

    // The OASIS schema carries a Skin section for every discipline, but only the
    // visit types WellSky gives a wound section get the Wounds module.
    if (category === SKIN_SECTION_ID && supportsWounds(visitType)) {
      return (
        <div className='mt-6 pt-6 border-t border-border'>
          <h4 className='text-sm font-semibold text-foreground mb-3'>Wounds</h4>
          {/* Flat on purpose: Active / Discontinued tabs unmount hidden
              rows, and Live Scrubber healed-wound rules jump by
              discontinued id (EMR-931). */}
          <PatientWounds
            patientId={patientId}
            visitId={visitId}
            visitStatus={visitStatus}
            episodeClosed={episodeClosed}
            onWoundsChanged={handleScrubberInputChanged}
          />
        </div>
      );
    }

    if (category === MEDICATIONS_SECTION_ID) {
      return (
        <>
          <div className='mt-6 pt-6 border-t border-border'>
            <h4 className='text-sm font-semibold text-foreground mb-3'>
              Medications
            </h4>
            <PatientMedications
              patientId={patientId}
              episodeId={episodeId}
              visitId={visitId}
              visitStatus={visitStatus}
              episodeClosed={episodeClosed}
              onMedicationOrderCreated={handleMedicationOrderCreated}
              onMedicationsChanged={handleScrubberInputChanged}
            />
          </div>
          {patient && (
            <div className='mt-6 pt-6 border-t border-border'>
              <h4 className='text-sm font-semibold text-foreground mb-3'>
                Allergies
              </h4>
              <PatientAllergies patient={patient} />
            </div>
          )}
        </>
      );
    }

    return null;
  };

  const renderOasisSectionContent = () => {
    const BespokeComponent = resolveBespokeSection(
      bespokeDbConfig,
      schemaVersion?.name,
      currentCategory || ''
    );

    const standardGrid = (
      <div className='grid grid-cols-12 gap-3'>
        {gridItems.map(item => renderGridItem(item))}
      </div>
    );

    if (BespokeComponent && schemaVersion) {
      return (
        <>
          <div className='mb-4 pb-2 border-b border-border flex items-center justify-between'>
            <h3 className='text-base font-bold text-foreground'>
              {resolveCategoryLabel(currentCategory || '', labelByCategory)}
            </h3>
          </div>
          {renderCarryoverBar()}
          {renderInlineValidationPanel()}
          <QuestionGridFloor>
            <BespokeComponent
              answers={formData}
              onAnswerChange={(qid, value) =>
                handleFormDataChange({ [qid]: value })
              }
              onFormDataChange={handleFormDataChange}
              schema={currentQuestions}
              schemaQuestionOptions={schemaQuestionOptions}
              sectionName={currentCategory || ''}
              externalAnswers={externalAnswers}
              fallback={standardGrid}
            />
          </QuestionGridFloor>
          {renderEmbeddedSectionModules()}
        </>
      );
    }

    return (
      <>
        <div className='mb-4 pb-2 border-b border-border flex items-center justify-between'>
          <h3 className='text-base font-bold text-foreground'>
            {resolveCategoryLabel(currentCategory || '', labelByCategory)}
          </h3>
        </div>
        {renderCarryoverBar()}
        {renderInlineValidationPanel()}

        <QuestionGridFloor>{standardGrid}</QuestionGridFloor>
        {renderEmbeddedSectionModules()}
      </>
    );
  };

  // ─── Non-OASIS: single merged schema bucket (`All`); section chrome → layout_json ───
  if (!isOasis) {
    const wholeSectionKey = categories[0] ?? NON_OASIS_WHOLE_VISIT_SECTION_NAME;
    const categoryQuestions = schema[wholeSectionKey] || [];
    const items = flattenQuestions(categoryQuestions);
    const BespokeComponent = schemaVersion
      ? resolveBespokeSection(
          bespokeDbConfig,
          schemaVersion.name,
          wholeSectionKey
        )
      : null;

    const standardGrid = (
      <div className='grid grid-cols-12 gap-3'>
        {items.map(item => renderGridItem(item))}
      </div>
    );

    return (
      <div ref={scrollportRef} className='min-w-0'>
        {renderCarryoverBar()}
        {renderInlineValidationPanel()}
        <QuestionGridFloor>
          {BespokeComponent ? (
            <BespokeComponent
              answers={formData}
              onAnswerChange={(qid, value) =>
                handleFormDataChange({ [qid]: value })
              }
              onFormDataChange={handleFormDataChange}
              schema={categoryQuestions}
              schemaQuestionOptions={schemaQuestionOptions}
              sectionName={wholeSectionKey}
              externalAnswers={externalAnswers}
              fallback={standardGrid}
            />
          ) : (
            standardGrid
          )}
        </QuestionGridFloor>
        {/* Routine visits are a single page, so the medications, allergies,
            wounds, and goals modules are surfaced inline at the bottom of the
            form, matching the OASIS section embeds. This lets clinicians review
            and edit them before submitting the chart instead of leaving the
            form to reach separate tabs. Each module self-gates editing by visit
            status, so it stays read-only where it is not eligible.

            Medications and allergies are scoped to skilled-nursing (RN/SN)
            routine notes: the non-OASIS branch also renders PT/OT/ST/MSW/HHA
            notes, and those clinical modules do not belong on those disciplines'
            forms. Wounds and goals use their own per-visit-type lists instead,
            since WellSky puts a wound section on PT/OT notes but not on ST,
            supervisory, MSW, or aide notes. */}
        {isSNDiscipline(visitType) && (
          <>
            <div className='mt-6 pt-6 border-t border-border'>
              <h3 className='text-base font-bold text-foreground mb-3'>
                Medications
              </h3>
              {/* No onMedicationsChanged here on purpose: this is the routine
                  (non-OASIS) single-page render, and the Live Scrubber only
                  runs on OASIS visits. Only the OASIS embeds re-validate. */}
              <PatientMedications
                patientId={patientId}
                episodeId={episodeId}
                visitId={visitId}
                visitStatus={visitStatus}
                episodeClosed={episodeClosed}
                onMedicationOrderCreated={handleMedicationOrderCreated}
              />
            </div>
            {patient && (
              <div className='mt-6 pt-6 border-t border-border'>
                <h3 className='text-base font-bold text-foreground mb-3'>
                  Allergies
                </h3>
                <PatientAllergies patient={patient} />
              </div>
            )}
          </>
        )}
        {supportsWounds(visitType) && (
          <div className='mt-6 pt-6 border-t border-border'>
            <h3 className='text-base font-bold text-foreground mb-3'>Wounds</h3>
            {/* No onWoundsChanged here on purpose — see the medications note
                above: the Live Scrubber only runs on OASIS visits. */}
            {/* Flat on purpose — see the OASIS Skin embed. Live Scrubber
                does not run here, but the table should match that surface. */}
            <PatientWounds
              patientId={patientId}
              visitId={visitId}
              visitStatus={visitStatus}
              episodeClosed={episodeClosed}
            />
          </div>
        )}
        {showsDiagnosisReview(visitType) && (
          <div className='mt-6 pt-6 border-t border-border'>
            <h3 className='text-base font-bold text-foreground mb-3'>
              Diagnoses
            </h3>
            {/* Read-only. This note displays the episode's diagnoses; it does
                not record them — see showsDiagnosisReview. */}
            <DiagnosisReview visitId={visitId} />
          </div>
        )}
        {showsGoalsSection(visitType) && (
          <div className='mt-6 pt-6 border-t border-border'>
            <h3 className='text-base font-bold text-foreground mb-3'>
              Goals &amp; Interventions
            </h3>
            {renderGoalsSection()}
          </div>
        )}
        {showsEventsSection(visitType) && (
          <div className='mt-6 pt-6 border-t border-border'>
            <h3 className='text-base font-bold text-foreground mb-3'>Events</h3>
            {renderEventsSection()}
          </div>
        )}
      </div>
    );
  }
  return (
    <div ref={scrollportRef} className='flex flex-col md:flex-row'>
      {/* Mobile: collapsible section picker */}
      <div className='md:hidden mb-4'>
        <button
          type='button'
          onClick={() => setIsMobileNavOpen(open => !open)}
          aria-expanded={isMobileNavOpen}
          aria-controls='oasis-mobile-section-list'
          className='w-full flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2.5 text-sm font-medium text-left'
        >
          <span className='truncate'>
            {resolveCategoryLabel(currentCategory || '', labelByCategory)}
          </span>
          {isMobileNavOpen ? (
            <ChevronUp className='h-4 w-4 shrink-0 text-muted-foreground' />
          ) : (
            <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
          )}
        </button>
        {isMobileNavOpen && (
          <div
            id='oasis-mobile-section-list'
            className='mt-2 rounded-md border border-border p-2 max-h-80 overflow-y-auto'
          >
            <OASISNavigation
              categories={categories}
              currentCategory={currentCategory}
              onCategoryChange={category => {
                setIsMobileNavOpen(false);
                handleCategoryChange(category);
              }}
              schema={schema}
              formData={formData}
              fatalCountByCategory={fatalCountByCategory}
              advisoryCountByCategory={advisoryCountByCategory}
              labelByCategory={labelByCategory}
            />
          </div>
        )}
      </div>

      {/* Sidebar navigation (desktop). Pinned to the top of the scrollport so
          sections stay switchable from anywhere in the form. The list is often
          taller than the scrollport, and a pinned element taller than its
          scrollport can never be scrolled into view, so it is capped to the
          measured height and scrolls within itself instead. */}
      <div className='hidden md:block w-48 flex-shrink-0 pr-4 border-r border-border'>
        {/* `top-5` mirrors the `p-5` on the scrolled content in
            `VisitDetailPanel`, so the list parks where it already sits rather
            than sliding that padding's worth on the first scroll. Same rem
            unit, so the two stay together if the root font size changes. Only
            this list can do it -- nothing shares its column, so the strip above
            stays empty, where the Live Scrubber would show form fields. */}
        <div
          className='sticky top-5 flex flex-col overflow-y-auto'
          style={scrollportHeight ? { maxHeight: scrollportHeight } : undefined}
        >
          <div className='flex items-center justify-between mb-2 px-2.5'>
            <span className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wider'>
              Sections
            </span>
          </div>
          <OASISNavigation
            categories={categories}
            currentCategory={currentCategory}
            onCategoryChange={handleCategoryChange}
            schema={schema}
            formData={formData}
            fatalCountByCategory={fatalCountByCategory}
            advisoryCountByCategory={advisoryCountByCategory}
            labelByCategory={labelByCategory}
          />
        </div>
      </div>

      {/* Form content */}
      <div className='flex-1 min-w-0 md:pl-5'>
        {currentCategory === ACTIVE_DIAGNOSIS_CATEGORY ? (
          <>
            <div className='mb-4 pb-2 border-b border-border'>
              <h3 className='text-base font-bold text-foreground'>
                Active Diagnosis
              </h3>
            </div>
            {/* Same Live Scrubber chrome as OASIS sections — including CMS
                fatal mode, even though Active Diagnosis has no CMS qids. */}
            {renderCarryoverBar()}
            {renderInlineValidationPanel()}
            <VisitActiveDiagnosis
              visitId={visitId}
              onActiveDiagnosesSaved={handleScrubberInputChanged}
            />
          </>
        ) : currentCategory === GOALS_CATEGORY ? (
          <>
            <div className='mb-4 pb-2 border-b border-border'>
              <h3 className='text-base font-bold text-foreground'>
                Goals &amp; Interventions
              </h3>
            </div>
            {renderGoalsSection()}
          </>
        ) : currentCategory === EVENTS_CATEGORY ? (
          <>
            <div className='mb-4 pb-2 border-b border-border'>
              <h3 className='text-base font-bold text-foreground'>Events</h3>
            </div>
            {renderEventsSection()}
          </>
        ) : (
          renderOasisSectionContent()
        )}
      </div>
    </div>
  );
};

/**
 * Loads the agency's template library and publishes it to the text fields.
 *
 * A component of its own rather than a hook called in the wrapper below, so the
 * fetch state lives here: state in the memoized wrapper would re-render the
 * whole question tree when the library arrives, which is the cost the memo
 * exists to avoid.
 */
function FormTemplatesLoader({ children }: { children: React.ReactNode }) {
  const templates = useAgencyFormTemplates();
  return (
    <FormTemplatesProvider templates={templates}>
      {children}
    </FormTemplatesProvider>
  );
}

/**
 * Memoized so the parent's per-keystroke status state updates do not
 * re-render the whole question tree; keep the function props stable.
 *
 * The clinic and template providers wrap the form rather than living inside it
 * so every early return is covered by one mount.
 */
export const OASISForm = React.memo(function OASISForm(props: OASISFormProps) {
  return (
    <ClinicProvider agencyClinicId={props.agencyClinicId ?? null}>
      <FormTemplatesLoader>
        <OASISFormComponent {...props} />
      </FormTemplatesLoader>
    </ClinicProvider>
  );
});
OASISForm.displayName = 'OASISForm';
